// Hook para leer adherencia real de un paciente (esperadas vs completadas)
// + score promedio para una ventana, vía RPC get_adherencia_paciente
// (migration 0018).
//
// Sprint 4 · Build "Adherencia real end-to-end (fase 1 analytics)".
//
// Uso:
//   const { data, loading, error } = useAdherenciaPaciente(pacienteId, 'total');
//   if (loading) return <Skeleton />;
//   if (!data || data.code === 'NO_PLAN_ACTIVO') return <FallbackSinPlan />;
//   if (data.has_frecuencia === false) return <FallbackSinFrecuencia />;
//   if (data.code === 'VENTANA_MUY_CORTA') return <FallbackVentanaCorta />;
//   return <>{data.adherencia_pct}%</>;
//
// Nota: DashboardKine NO usa este hook (usa Promise.all inline dentro de
// useKineDashboard para batchear N pacientes en una sola pasada). Ver
// SPEC A5.

import { useEffect, useState } from "react";
import { supabase } from "../client";

/** Ventana ratificada (Q7 + A1). ENUM Postgres refleja mismo set. */
export type VentanaAdherencia = "7d" | "30d" | "total";

/** Codes del retorno de la RPC. */
export type AdherenciaCode =
  | "NO_AUTH"
  | "NOT_FOUND"
  | "NO_PLAN_ACTIVO"
  | "VENTANA_MUY_CORTA"
  | "UNKNOWN";

/** Shape del retorno JSONB. Todos los numéricos pueden ser null según
 *  el path. Ver SPEC sección 3 F-04 y sección 7 rubric #3-#8 para la
 *  matriz completa de shapes.
 *
 *  Estados sin `code` explícito (ok:true y hay data legítima) se
 *  distinguen por `has_frecuencia`:
 *  - has_frecuencia=false → no se puede calcular %, mostrar completadas.
 *  - has_frecuencia=true + adherencia_pct=null → VENTANA_MUY_CORTA (code presente).
 *  - has_frecuencia=true + adherencia_pct número → happy path. */
export interface AdherenciaResult {
  ok: boolean;
  code?: AdherenciaCode;
  /** SQLERRM en caso UNKNOWN. Deuda técnica conocida (SQLERRM leakage). */
  detail?: string;
  adherencia_pct: number | null;
  score_promedio: number | null;
  sesiones_completadas: number | null;
  sesiones_esperadas: number | null;
  ventana: string;
  since: string | null;
  until: string | null;
  has_frecuencia: boolean;
}

interface State {
  data: AdherenciaResult | null;
  loading: boolean;
  error: string | null;
}

export interface UseAdherenciaPacienteReturn extends State {}

/** Devuelve la adherencia + score del paciente para la ventana pasada.
 *  Re-fetchea cuando pacienteId o ventana cambian. Sin cache TTL (A6).
 *  Si pacienteId es null/undefined, queda en loading=false + data=null
 *  sin disparar fetch. */
export function useAdherenciaPaciente(
  pacienteId: string | null | undefined,
  ventana: VentanaAdherencia,
): UseAdherenciaPacienteReturn {
  const [state, setState] = useState<State>({
    data: null,
    loading: !!pacienteId,
    error: null,
  });

  useEffect(() => {
    if (!pacienteId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    const signal = { cancelled: false };
    setState((s) => ({ ...s, loading: true, error: null }));

    void (async () => {
      const { data, error } = await supabase.rpc("get_adherencia_paciente", {
        p_paciente_id: pacienteId,
        p_ventana: ventana,
      });

      if (signal.cancelled) return;

      if (error) {
        console.warn("[useAdherenciaPaciente] rpc error", error);
        setState({ data: null, loading: false, error: error.message });
        return;
      }

      // La RPC devuelve JSONB con shape estable. Cast defensivo.
      setState({
        data: (data as AdherenciaResult) ?? null,
        loading: false,
        error: null,
      });
    })();

    return () => {
      signal.cancelled = true;
    };
  }, [pacienteId, ventana]);

  return state;
}
